import Big from 'big.js'
import { Dots } from 'metronome-ui/components/Dots'
import { useChainId } from 'metronome-ui/hooks/chains'
import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useContext,
  useEffect
} from 'react'

import { useWalletChainId } from '../../hooks/useWalletChainId'
import { AppContext } from '../AppContext/AppContext'
import CryptoNumber from '../CryptoNumber'
import Dropdown from '../Dropdown'
import IconContainer from '../icons/IconContainer'
import { MetronomeContext } from '../MetronomeContext'

import { AvailableToken } from './CryptoInputGroup'

export type TokenSelectorProps = {
  availableTokens: AvailableToken[]
  initialSelectedTokenSymbol?: string
  selectedToken: AvailableToken
  setSelectedTokenIndex?: Dispatch<SetStateAction<number>>
  chooseLabel?: string
  className?: string
  disableWithoutBalance?: boolean
  disabled?: boolean
  displayBalances?: boolean
  onChange?: (selectedToken: AvailableToken) => void
  onBlur?: () => void
}

function TokenSelector({
  availableTokens = [],
  chooseLabel = 'Select',
  initialSelectedTokenSymbol,
  selectedToken,
  setSelectedTokenIndex,
  className,
  disableWithoutBalance = true,
  disabled,
  displayBalances = true,
  onChange,
  onBlur
}: TokenSelectorProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { walletBalancesLoaded } = useContext<any>(MetronomeContext)
  const chainId = useChainId()
  const walletChainId = useWalletChainId()
  const {
    state: {
      wallet: { connected }
    }
  } = useContext(AppContext)
  // Set the selectedToken from initialSelectedToken
  useEffect(
    function () {
      if (initialSelectedTokenSymbol && !selectedToken) {
        const _selectedTokenIndex = availableTokens.findIndex(
          t => t.symbol === initialSelectedTokenSymbol
        )
        setSelectedTokenIndex(_selectedTokenIndex)
      }
    },
    [
      availableTokens,
      initialSelectedTokenSymbol,
      setSelectedTokenIndex,
      selectedToken
    ]
  )

  const handleChange = useCallback(
    function (newValue) {
      const _selectedTokenIndex = availableTokens.findIndex(
        t => t.symbol === newValue
      )
      setSelectedTokenIndex(_selectedTokenIndex)
      if (availableTokens[_selectedTokenIndex]) {
        onChange?.(availableTokens[_selectedTokenIndex])
      }
    },
    [availableTokens, onChange, setSelectedTokenIndex]
  )

  // When there's one single option, emit the value as selected right away
  useEffect(
    function emitSingleValue() {
      if (availableTokens.length === 1) {
        handleChange(availableTokens[0].symbol)
      }
    },
    [availableTokens, handleChange]
  )

  return (
    <Dropdown
      className={`whitespace-nowrap ${className ? className : ''}`}
      disabled={disabled}
      selector={
        availableTokens.length === 1 ? (
          <button
            className={`${
              disabled ? 'cursor-not-allowed opacity-50' : 'cursor-default'
            } bg-blue-tertiary flex items-center rounded-lg px-3 py-2 text-sm leading-6 text-white`}
            onBlur={onBlur}
            type="button"
          >
            <IconContainer
              className="mr-3 h-6 w-6"
              name={availableTokens[0].symbol.toLowerCase()}
            />
            {availableTokens[0].symbol}
          </button>
        ) : selectedToken ? (
          <div
            className={`${
              disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            } bg-blue-tertiary flex items-center rounded-lg px-3 py-2 text-sm leading-6 text-white`}
            data-sentry="token-selector"
          >
            <IconContainer
              className="mr-3 h-6 w-6"
              name={selectedToken.symbol.toLowerCase()}
            />
            {selectedToken.symbol}
            <IconContainer className="ml-3 fill-current" name="caret" />
          </div>
        ) : (
          <button
            className={`${
              disabled ? 'cursor-not-allowed opacity-50' : ''
            } bg-blue-tertiary flex items-center rounded-lg px-3 py-2 text-sm leading-6 text-white`}
            data-sentry="token-selector"
            onBlur={onBlur}
            type="button"
          >
            {chooseLabel}
            <IconContainer className="ml-3 fill-current" name="caret" />
          </button>
        )
      }
    >
      <div
        className={`bg-blue-tertiary absolute right-0 z-40 mt-3 w-72 rounded-lg ${
          '' /*itemsListClassName*/ /* @TODO receive this prop */
        }`}
      >
        {availableTokens.length > 1 && (
          <ul>
            {availableTokens.map(function (availableToken) {
              const hasBalance =
                availableToken?.balance && Big(availableToken.balance).gt(0)
              const noSupply = Big(availableToken.availableSupply).lte(0)

              return (
                <li
                  className={`hover:bg-blue-4 m-2 cursor-pointer rounded-lg p-2 text-white ${
                    (displayBalances && !hasBalance && disableWithoutBalance) ||
                    !availableToken.isActive ||
                    noSupply
                      ? 'cursor-not-allowed opacity-50'
                      : ''
                  }`}
                  data-sentry="token-selector-item"
                  key={availableToken.address}
                  onClick={function (e) {
                    if (
                      (displayBalances &&
                        !hasBalance &&
                        disableWithoutBalance) ||
                      !availableToken.isActive ||
                      noSupply
                    ) {
                      e.stopPropagation()
                      return
                    }
                    handleChange(availableToken.symbol)
                  }}
                >
                  <div className="flex justify-between">
                    <span className="flex">
                      <IconContainer
                        className="mr-2"
                        height={24}
                        name={availableToken.symbol.toLowerCase()}
                        width={24}
                      />
                      {availableToken.symbol}
                    </span>
                    <span className="text-sm">
                      {displayBalances &&
                        (chainId === walletChainId &&
                        connected &&
                        !walletBalancesLoaded ? (
                          <Dots />
                        ) : (
                          availableToken?.balance &&
                          (Big(availableToken.balance).gt(0) ? (
                            <CryptoNumber value={availableToken.balance} />
                          ) : (
                            <span className="mr-4">-</span>
                          ))
                        ))}
                    </span>
                  </div>
                  {/*
                  @TODO: implement estimatedApy and messages. We could use render props to allow the user of TokenSelector to be able to render each item as desired
                  {availableToken.message && (
                    <p className="whitespace-normal pl-8 text-xs">
                      {availableToken.message}
                    </p>
                  )}
                  {availableToken.estimatedApy && (
                    <p className="whitespace-normal pl-8 text-xs">
                      {availableToken.estimatedApy.text}:{' '}
                      {availableToken.estimatedApy.apy}% -{' '}
                      {availableToken.estimatedApy.maxLeveragedApy.lt(0) ? (
                        <span className="text-red-600">
                        {availableToken.estimatedApy.maxLeveragedApy.toFixed()}
                          %
                      </span>
                      ) : (
                        <>
                          {availableToken.estimatedApy.maxLeveragedApy.toFixed()}
                          %
                        </>
                      )}
                    </p>
                  )}*/}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </Dropdown>
  )
}

export default memo(TokenSelector)
